import React from "react";
import { motion, useInView } from "framer-motion";

export function CountUp({ value, duration = 1.2, prefix = "", suffix = "" }) {
  const ref = React.useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const [display, setDisplay] = React.useState(0);

  React.useEffect(() => {
    if (!inView) return;
    const target = parseFloat(String(value).replace(/[^\d.]/g, "")) || 0;
    const start = performance.now();
    let raf;
    const tick = (t) => {
      const elapsed = (t - start) / 1000;
      const p = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(target * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
      else setDisplay(target);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, value, duration]);

  const isInt = !String(value).includes(".");
  const shown = isInt ? Math.round(display).toLocaleString() : display.toFixed(2);
  return (
    <motion.span ref={ref} className="tabular-nums">
      {prefix}
      {shown}
      {suffix}
    </motion.span>
  );
}

export function WordCycler({ words, className = "" }) {
  const [i, setI] = React.useState(0);
  React.useEffect(() => {
    const t = setInterval(() => setI((v) => (v + 1) % words.length), 2200);
    return () => clearInterval(t);
  }, [words.length]);
  return (
    <span className={`inline-flex align-baseline ${className}`}>
      <span className="relative inline-block">
        <span className="invisible whitespace-pre">
          {words.reduce((a, b) => (a.length > b.length ? a : b))}
        </span>
        {words.map((w, idx) => (
          <motion.span
            key={w}
            className="absolute inset-0 whitespace-pre"
            initial={{ y: "100%", opacity: 0 }}
            animate={{
              y: idx === i ? "0%" : idx < i ? "-100%" : "100%",
              opacity: idx === i ? 1 : 0,
            }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
          >
            {w}
          </motion.span>
        ))}
      </span>
    </span>
  );
}

export function Marquee({ children, className = "" }) {
  return (
    <div className={`overflow-hidden relative ${className}`}>
      <div className="flex marquee-track whitespace-nowrap">
        <div className="flex items-center gap-10 pr-10">{children}</div>
        <div className="flex items-center gap-10 pr-10" aria-hidden="true">
          {children}
        </div>
      </div>
    </div>
  );
}
